/**
 * `PWA-052`/`PROG-008` (request persistence on first completion) and
 * `PWA-053` (Settings reports whether it was granted). Two separate browser
 * calls -- `navigator.storage.persist()` asks, `navigator.storage.persisted()`
 * reports the current answer -- kept as two functions rather than one for the
 * same reason: a Settings visit that merely wants to *read* the current state
 * must never re-ask, since asking again is exactly the kind of repeated
 * permission prompt `PWA-044`'s own reasoning already argues against for
 * updates.
 *
 * Neither call throws in a browser that lacks the Storage API at all --
 * `navigator.storage` itself is checked first, the same "does this
 * environment actually have the capability" guard `install.ts`'s
 * `detectStandaloneDisplay` uses for `matchMedia`.
 */

import { signal } from '@preact/signals';

function hasStorageManager(): boolean {
  return typeof navigator !== 'undefined' && 'storage' in navigator;
}

/**
 * `PWA-052`/`PROG-008`. Fire-and-forget, called once from `Play.tsx`'s
 * `onComplete` when `completion.ts`'s `isFirstCompletionEver` says this is
 * the moment -- a browser's own engagement heuristics decide whether the
 * request is actually granted, which is why this and `PWA-053` are
 * `Manual`: what a real browser grants under real, organic use is not
 * something a scripted session reproduces.
 */
export function requestPersistentStorage(): void {
  if (!hasStorageManager()) return;
  void navigator.storage.persist().then((granted) => {
    storagePersisted.value = granted;
  });
}

/** `PWA-053`. `null` means "not yet known" (unsupported, or not asked this
 * session) -- `Settings.tsx` treats that as its own, honest third state
 * rather than defaulting to either boolean. */
export const storagePersisted = signal<boolean | null>(null);

/** Reads the current grant without requesting it -- `Settings.tsx`'s own
 * mount effect, so a visit that never triggers `requestPersistentStorage`
 * (no level completed yet) still reports an accurate, current answer. */
export function refreshStoragePersisted(): void {
  if (!hasStorageManager()) return;
  void navigator.storage.persisted().then((granted) => {
    storagePersisted.value = granted;
  });
}
